import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardSidebar } from '../components/DashboardSidebar';
import { SupportTicketPanel } from '../components/SupportTicketPanel';
import { Server, CreditCard, LifeBuoy, Activity, Download, ExternalLink } from 'lucide-react';
export function CustomerDashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const stats = [{
    label: 'Active Services',
    value: '3',
    icon: Server,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10'
  }, {
    label: 'Next Invoice',
    value: '$24.97',
    icon: CreditCard,
    color: 'text-fox-500',
    bg: 'bg-fox-500/10'
  }, {
    label: 'Support Tickets',
    value: '1',
    icon: LifeBuoy,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10'
  }, {
    label: 'Uptime',
    value: '99.9%',
    icon: Activity,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10'
  }];
  const services = [{
    name: 'Web Hosting Pro',
    domain: 'foxlinehosting.com',
    status: 'Active',
    renews: 'Jan 15, 2026',
    price: '$9.99/mo'
  }, {
    name: 'SSL Certificate',
    domain: 'foxlinehosting.com',
    status: 'Active',
    renews: 'Mar 22, 2026',
    price: '$4.99/yr'
  }, {
    name: 'Domain Registration',
    domain: 'foxlinehosting.com',
    status: 'Active',
    renews: 'Dec 10, 2026',
    price: '$12.99/yr'
  }];
  const invoices = [{
    id: 'INV-2025-001',
    date: 'Jan 15, 2025',
    amount: '$24.97',
    status: 'Paid'
  }, {
    id: 'INV-2024-012',
    date: 'Dec 15, 2024',
    amount: '$24.97',
    status: 'Paid'
  }, {
    id: 'INV-2024-011',
    date: 'Nov 15, 2024',
    amount: '$24.97',
    status: 'Paid'
  }];
  const Overview = () => <>
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-10" data-id="element-1735">
        <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1736">
          Welcome back, Sarah!
        </h1>
        <p className="text-slate-400" data-id="element-1737">
          Here's an overview of your hosting account.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-1738">
        {stats.map((stat, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1739">
            <div className="flex items-center justify-between mb-4" data-id="element-1740">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color}`} data-id="element-1741">
                <stat.icon size={24} data-id="element-1742" />
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1" data-id="element-1743">{stat.value}</h3>
            <p className="text-slate-400 text-sm font-medium" data-id="element-1744">{stat.label}</p>
          </motion.div>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1745">
        {/* Services List */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2 space-y-6" data-id="element-1746">
          <div className="flex items-center justify-between" data-id="element-1747">
            <h2 className="text-xl font-heading font-bold text-white" data-id="element-1748">
              My Services
            </h2>
            <button className="text-sm text-fox-500 hover:text-fox-400 font-medium" data-id="element-1749">
              View All
            </button>
          </div>

          <div className="space-y-4" data-id="element-1750">
            {services.map((service, i) => <div key={i} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-slate-700 transition-colors" data-id="element-1751">
                <div data-id="element-1752">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1753">
                    <h3 className="text-lg font-bold text-white" data-id="element-1754">
                      {service.name}
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium border border-emerald-500/20" data-id="element-1755">
                      {service.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400" data-id="element-1756">
                    <span data-id="element-1757">{service.domain}</span>
                    <span data-id="element-1758">•</span>
                    <span data-id="element-1759">Renews {service.renews}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4" data-id="element-1760">
                  <span className="font-medium text-white" data-id="element-1761">
                    {service.price}
                  </span>
                  <button className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2" data-id="element-1762">
                    Manage <ExternalLink size={14} data-id="element-1763" />
                  </button>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Recent Invoices */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="space-y-6" data-id="element-1764">
          <div className="flex items-center justify-between" data-id="element-1765">
            <h2 className="text-xl font-heading font-bold text-white" data-id="element-1766">
              Recent Invoices
            </h2>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-1767">
            <div className="divide-y divide-slate-800" data-id="element-1768">
              {invoices.map((invoice, i) => <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-900/50 transition-colors" data-id="element-1769">
                  <div data-id="element-1770">
                    <p className="text-sm font-medium text-white mb-1" data-id="element-1771">
                      {invoice.id}
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-1772">{invoice.date}</p>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-1773">
                    <span className="text-sm font-medium text-white" data-id="element-1774">
                      {invoice.amount}
                    </span>
                    <button className="text-slate-400 hover:text-fox-500 transition-colors" data-id="element-1775">
                      <Download size={18} data-id="element-1776" />
                    </button>
                  </div>
                </div>)}
            </div>
            <div className="p-4 border-t border-slate-800 bg-slate-900/50" data-id="element-1777">
              <button className="w-full text-sm text-center text-fox-500 hover:text-fox-400 font-medium" data-id="element-1778">
                View Billing History
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </>;
  return <div className="min-h-screen bg-slate-900 flex" data-id="element-1779">
      <DashboardSidebar type="customer" isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} data-id="element-1780" />

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0" data-id="element-1781">
        <div className="p-6 md:p-10 max-w-6xl mx-auto" data-id="element-1782">
          <Routes data-id="element-1783">
            <Route path="/" element={<Overview data-id="element-1785" />} data-id="element-1784" />
            <Route path="/support" element={<SupportTicketPanel userType="customer" userName="Sarah Jenkins" userEmail="sarah@example.com" data-id="element-1787" />} data-id="element-1786" />
            <Route path="*" element={<Navigate to="/dashboard/customer" replace data-id="element-1789" />} data-id="element-1788" />
          </Routes>
        </div>
      </main>
    </div>;
}